import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import clinicalTrialsService from "../../services/clinicalTrialsService";

// Import the TrialCard component from ClinicalTrialsSearch
import { TrialCard } from "./ClinicalTrialsSearch";

const PatientMatchingPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [matchedTrials, setMatchedTrials] = useState([]);
  const [patientId, setPatientId] = useState(null);
  // Patient Information State
  const [patientInfo, setPatientInfo] = useState({
    // Personal Information
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    age: "",
    gender: "",
    email: "",
    phone: "",

    // Address
    address: "",
    city: "",
    state: "",
    zipCode: "",

    // Medical Information
    ckdStage: "",
    ckdEtiology: "",
    targetOrgan: "",
    diagnosisDate: "",
    dialysisModality: "",
    previousTreatments: [],
    currentMedications: [],
    comorbidities: [],

    // Additional Info
    performanceStatus: "",
    willingToTravel: false,
  });

  // Matching Criteria State
  const [matchingCriteria, setMatchingCriteria] = useState({
    ckdStage: "",
    targetOrgan: "",
    trialPhase: "",
    trialStatus: "Recruiting",
    maxTravelDistance: "50",
    minMatchScore: "60",
  });

  // Handle Patient Info Changes
  const handlePatientInfoChange = (e) => {
    const { name, value, checked, type } = e.target;
    setPatientInfo((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle Matching Criteria Changes
  const handleCriteriaChange = (e) => {
    const { name, value } = e.target;
    setMatchingCriteria((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Auto-populate matching criteria from patient info
  const syncCriteriaWithPatient = () => {
    setMatchingCriteria((prev) => ({
      ...prev,
      ckdStage: patientInfo.ckdStage,
      targetOrgan: patientInfo.targetOrgan,
    }));
  };

  // Handle Match Button Click (Step 4a: Call API)
const handleMatchTrials = async () => {
  try {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setMatchedTrials([]);

    // Validate required fields
    if (
      !patientInfo.firstName ||
      !patientInfo.lastName ||
      !patientInfo.age ||
      !patientInfo.gender ||
      !patientInfo.ckdStage
    ) {
      setError(
        "Please fill in all required fields (Name, Age, Gender, CKD Stage)"
      );
      setLoading(false);
      return;
    }

    // Prepare data for API call
    const matchData = {
      ...patientInfo,
      matchingCriteria: matchingCriteria,
    };

    console.log("Matching trials with:", matchData);

    // Call API
    const response = await clinicalTrialsService.matchPatientToTrials(
      matchData
    );
    console.log("Response is", response);
    console.log("Match trials are ", response.matchedTrials);

    // Check if we got trials
    if (
      response &&
      response.matchedTrials &&
      response.matchedTrials.length > 0
    ) {
      // Transform trials to display format
      const trialsWithSource = response.matchedTrials.map((match) => ({
        ...match.trial,
        matchScore: match.matchScore,
        matchReasons: match.matchReasons,
        source: "internal",
      }));

      setMatchedTrials(trialsWithSource);
      setSuccessMessage(
        `Found ${trialsWithSource.length} matching clinical trials!`
      );

      console.log(`Successfully matched ${trialsWithSource.length} trials`);
    } else {
      setError("No matching trials found for the given criteria");
      setMatchedTrials([]);
    }
  } catch (err) {
    console.error("Error matching patient:", err);
    setError("Error finding matching trials. Please try again.");
    setMatchedTrials([]);
  } finally {
    setLoading(false);
  }
};

  // Handle Save Button Click (Step 5: Save patient and matches)
  const handleSavePatient = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Validate that we have matched trials
      if (matchedTrials.length === 0) {
        setError("Please match trials first before saving");
        setSaving(false);
        return;
      }

      // Validate required fields
      if (
        !patientInfo.firstName ||
        !patientInfo.lastName ||
        !patientInfo.age ||
        !patientInfo.gender ||
        !patientInfo.ckdStage
      ) {
        setError("Please fill in all required patient information");
        setSaving(false);
        return;
      }

      // Extract trial IDs from matched trials
      const matchedTrialIds = matchedTrials
        .map((trial) => trial.id)
        .filter((id) => id);

      console.log("Saving patient with trials:", matchedTrialIds);

      // Prepare data with matching criteria
      const patientData = {
        ...patientInfo,
        matchingCriteria: matchingCriteria,
      };

      // Step 5: Call API to save patient and relationships
      const response = await clinicalTrialsService.savePatientAndMatches(
        patientData,
        matchedTrialIds
      );

      if (response.success) {
        setPatientId(response.data.patientInfo.patientId);
        setSuccessMessage(
          `Patient saved successfully! Patient ID: ${response.data.patientInfo.patientId}. ` +
            `Matched to ${matchedTrialIds.length} clinical trials.`
        );
        console.log(
          "Patient saved with ID:",
          response.data.patientInfo.patientId
        );
      } else {
        setError(response.message || "Failed to save patient");
      }
    } catch (err) {
      console.error("Error saving patient:", err);
      setError("Error saving patient information. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 4,
            background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            color: "white",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            Patient-Trial Matching
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Enter patient information to find matching clinical trials
          </Typography>
        </Paper>

        {/* Success Message */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
            {successMessage}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Side - Patient Information Form */}
          <Grid item xs={12} lg={6}>
            {/* Section 1: Personal Information */}
            <Card elevation={3} sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "primary.main",
                    mb: 3,
                  }}
                >
                  <Chip label="1" color="primary" size="small" />
                  <PersonIcon />
                  Personal Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="First Name"
                      name="firstName"
                      value={patientInfo.firstName}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Last Name"
                      name="lastName"
                      value={patientInfo.lastName}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Date of Birth"
                      name="dateOfBirth"
                      type="date"
                      value={patientInfo.dateOfBirth}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Age"
                      name="age"
                      type="number"
                      value={patientInfo.age}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      helperText="Years"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Gender"
                      name="gender"
                      value={patientInfo.gender}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={patientInfo.email}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={patientInfo.phone}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      placeholder="(555) 123-4567"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Section 2: Address Information */}
            <Card elevation={3} sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "success.main",
                    mb: 3,
                  }}
                >
                  <Chip label="2" color="success" size="small" />
                  Address Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="address"
                      value={patientInfo.address}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={patientInfo.city}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={patientInfo.state}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      placeholder="IL"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="zipCode"
                      value={patientInfo.zipCode}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={patientInfo.willingToTravel}
                          onChange={handlePatientInfoChange}
                          name="willingToTravel"
                          color="primary"
                        />
                      }
                      label="Willing to Travel for Treatment"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Section 3: Medical Information */}
            <Card elevation={3} sx={{ mb: 3 }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "warning.main",
                    mb: 3,
                  }}
                >
                  <Chip label="3" color="warning" size="small" />
                  <LocalHospitalIcon />
                  Medical Information
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="CKD Stage"
                      name="ckdStage"
                      value={patientInfo.ckdStage}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    >
                      <MenuItem value="">Select Stage</MenuItem>
                      <MenuItem value="1">Stage 1</MenuItem>
                      <MenuItem value="2">Stage 2</MenuItem>
                      <MenuItem value="3a">Stage 3a</MenuItem>
                      <MenuItem value="3b">Stage 3b</MenuItem>
                      <MenuItem value="4">Stage 4</MenuItem>
                      <MenuItem value="5">Stage 5</MenuItem>
                      <MenuItem value="5D">Stage 5D (Dialysis-dependent)</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Dialysis Modality"
                      name="dialysisModality"
                      value={patientInfo.dialysisModality}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    >
                      <MenuItem value="">Select Modality</MenuItem>
                      <MenuItem value="Pre-dialysis">Pre-dialysis</MenuItem>
                      <MenuItem value="Hemodialysis">Hemodialysis</MenuItem>
                      <MenuItem value="Peritoneal Dialysis">Peritoneal Dialysis</MenuItem>
                      <MenuItem value="Transplant">Transplant</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Diagnosis Date"
                      name="diagnosisDate"
                      type="date"
                      value={patientInfo.diagnosisDate}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="CKD Etiology"
                      name="ckdEtiology"
                      value={patientInfo.ckdEtiology}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    >
                      <MenuItem value="">Select Cause</MenuItem>
                      <MenuItem value="Diabetic Nephropathy">Diabetic Nephropathy</MenuItem>
                      <MenuItem value="Hypertensive Nephrosclerosis">
                        Hypertensive Nephrosclerosis
                      </MenuItem>
                      <MenuItem value="Glomerulonephritis">Glomerulonephritis</MenuItem>
                      <MenuItem value="Polycystic Kidney Disease">Polycystic Kidney Disease</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      select
                      label="Performance Status (ECOG)"
                      name="performanceStatus"
                      value={patientInfo.performanceStatus}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      <MenuItem value="0">0 - Fully active</MenuItem>
                      <MenuItem value="1">
                        1 - Restricted in strenuous activity
                      </MenuItem>
                      <MenuItem value="2">
                        2 - Ambulatory, capable of self-care
                      </MenuItem>
                      <MenuItem value="3">3 - Limited self-care</MenuItem>
                      <MenuItem value="4">4 - Completely disabled</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Previous Treatments"
                      name="previousTreatments"
                      value={patientInfo.previousTreatments}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      multiline
                      rows={2}
                      placeholder="List any previous treatments or therapies"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Medications"
                      name="currentMedications"
                      value={patientInfo.currentMedications}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      multiline
                      rows={2}
                      placeholder="List all current medications"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Comorbidities"
                      name="comorbidities"
                      value={patientInfo.comorbidities}
                      onChange={handlePatientInfoChange}
                      variant="outlined"
                      multiline
                      rows={2}
                      placeholder="List other medical conditions (e.g., diabetes, hypertension)"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Matching Criteria */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ position: "sticky", top: 20 }}>
              {/* Matching Criteria Card */}
              <Card elevation={3} sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "info.main",
                      mb: 3,
                    }}
                  >
                    <Chip label="4" color="info" size="small" />
                    <FilterListIcon />
                    Matching Criteria
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={syncCriteriaWithPatient}
                    sx={{ mb: 3 }}
                  >
                    Auto-fill from Patient Info
                  </Button>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="CKD Stage"
                        name="ckdStage"
                        value={matchingCriteria.ckdStage}
                        onChange={handleCriteriaChange}
                        variant="outlined"
                      >
                        <MenuItem value="">Any Stage</MenuItem>
                        <MenuItem value="1">Stage 1</MenuItem>
                        <MenuItem value="2">Stage 2</MenuItem>
                        <MenuItem value="3a">Stage 3a</MenuItem>
                        <MenuItem value="3b">Stage 3b</MenuItem>
                        <MenuItem value="4">Stage 4</MenuItem>
                        <MenuItem value="5">Stage 5</MenuItem>
                        <MenuItem value="5D">Stage 5D</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Target Organ"
                        name="targetOrgan"
                        value={matchingCriteria.targetOrgan}
                        onChange={handleCriteriaChange}
                        variant="outlined"
                      >
                        <MenuItem value="">Any Organ</MenuItem>
                        <MenuItem value="Cardiac">Cardiac</MenuItem>
                        <MenuItem value="Renal">Renal</MenuItem>
                        <MenuItem value="Neurological">Neurological</MenuItem>
                        <MenuItem value="Multi-organ">Multi-organ</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Trial Phase"
                        name="trialPhase"
                        value={matchingCriteria.trialPhase}
                        onChange={handleCriteriaChange}
                        variant="outlined"
                      >
                        <MenuItem value="">Any Phase</MenuItem>
                        <MenuItem value="Phase 1">Phase 1</MenuItem>
                        <MenuItem value="Phase 2">Phase 2</MenuItem>
                        <MenuItem value="Phase 3">Phase 3</MenuItem>
                        <MenuItem value="Phase 4">Phase 4</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Trial Status"
                        name="trialStatus"
                        value={matchingCriteria.trialStatus}
                        onChange={handleCriteriaChange}
                        variant="outlined"
                      >
                        <MenuItem value="">Any Status</MenuItem>
                        <MenuItem value="Recruiting">Recruiting</MenuItem>
                        <MenuItem value="Not yet recruiting">
                          Not Yet Recruiting
                        </MenuItem>
                        <MenuItem value="Active, not recruiting">
                          Active, Not Recruiting
                        </MenuItem>
                      </TextField>
                    </Grid>

                    {patientInfo.willingToTravel && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          select
                          label="Maximum Travel Distance"
                          name="maxTravelDistance"
                          value={matchingCriteria.maxTravelDistance}
                          onChange={handleCriteriaChange}
                          variant="outlined"
                        >
                          <MenuItem value="25">25 miles</MenuItem>
                          <MenuItem value="50">50 miles</MenuItem>
                          <MenuItem value="100">100 miles</MenuItem>
                          <MenuItem value="250">250 miles</MenuItem>
                          <MenuItem value="500">500+ miles</MenuItem>
                        </TextField>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Minimum Match Score"
                        name="minMatchScore"
                        value={matchingCriteria.minMatchScore}
                        onChange={handleCriteriaChange}
                        variant="outlined"
                        helperText="Only show trials with this match percentage or higher"
                      >
                        <MenuItem value="50">50% or higher</MenuItem>
                        <MenuItem value="60">60% or higher</MenuItem>
                        <MenuItem value="70">70% or higher</MenuItem>
                        <MenuItem value="80">80% or higher</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Step 2: Match Clinical Trials Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    color="primary"
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onClick={handleMatchTrials}
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                    }}
                  >
                    {loading ? "Matching..." : "Match Clinical Trials"}
                  </Button>
                </CardContent>
              </Card>

              {/* Step 5: Save Patient Button */}
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main">
                    Save Patient Information
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Save patient details and matched trials to database
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    color="success"
                    startIcon={
                      saving ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    onClick={handleSavePatient}
                    disabled={saving || matchedTrials.length === 0}
                    sx={{
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                    }}
                  >
                    {saving ? "Saving..." : "Save Patient & Matches"}
                  </Button>
                  {matchedTrials.length === 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Match trials first to enable saving
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>

        {/* Step 4b: Display Matched Trials Results */}
        {loading && (
          <Box sx={{ textAlign: "center", py: 8, mt: 4 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Finding matching clinical trials...
            </Typography>
          </Box>
        )}

        {!loading && matchedTrials.length > 0 && (
          <Box sx={{ mt: 4 }}>
            {/* Results Summary */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "#e8f5e9",
                border: "2px solid #4caf50",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "success.main",
                  fontWeight: "bold",
                }}
              >
                <CheckCircleIcon fontSize="large" />
                Found {matchedTrials.length} Matching Clinical Trial
                {matchedTrials.length !== 1 ? "s" : ""}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                Based on the patient's medical profile and matching criteria
              </Typography>
            </Paper>

            {/* Matched Trials List - Same format as ClinicalTrialsSearch */}
            <Grid container spacing={3}>
              {matchedTrials.map((trial, index) => (
                <Grid item xs={12} key={`${trial.nctId || trial.id}-${index}`}>
                  <MatchedTrialCard trial={trial} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Container>
  );
};

// Matched Trial Card Component (Enhanced version of TrialCard with match score)
const MatchedTrialCard = ({ trial }) => {
  const [expanded, setExpanded] = React.useState(false);

  const matchScore = trial.matchScore || 0;
  const matchReasons = trial.matchReasons || [];

  const getMatchColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "info";
  };

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase() || "";
    if (statusUpper.includes("RECRUITING")) return "success";
    if (statusUpper.includes("ACTIVE")) return "warning";
    if (statusUpper.includes("COMPLETED")) return "info";
    return "default";
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ") || "Unknown";
  };

  return (
    <Card
      elevation={3}
      sx={{
        "&:hover": { boxShadow: 8 },
        border: `3px solid`,
        borderColor: `${getMatchColor(matchScore)}.main`,
      }}
    >
      <CardContent>
        {/* Match Score Badge */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            mb: 2,
          }}
        >
          <Chip
            icon={<CheckCircleIcon />}
            label={`${matchScore}% Match`}
            color={getMatchColor(matchScore)}
            sx={{ fontWeight: "bold", fontSize: "1rem", px: 1 }}
          />
          <Chip
            label={formatStatus(trial.status)}
            color={getStatusColor(trial.status)}
            size="small"
          />
        </Box>

        {/* Match Score Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={matchScore}
            color={getMatchColor(matchScore)}
            sx={{ height: 10, borderRadius: 2 }}
          />
        </Box>

        {/* Trial Title */}
        <Typography variant="h6" gutterBottom fontWeight="bold">
          {trial.title}
        </Typography>

        {/* NCT ID */}
        <Typography
          variant="body2"
          color="primary"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          NCT ID: {trial.nctId}
        </Typography>

        {/* Trial Details Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Phase
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {trial.phase || "N/A"}
            </Typography>
          </Grid>

          {trial.sponsor && (
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Sponsor
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {trial.sponsor}
              </Typography>
            </Grid>
          )}

          {trial.ckdStage && (
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Stage
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {trial.ckdStage}
              </Typography>
            </Grid>
          )}

          {trial.targetOrgan && (
            <Grid item xs={6} sm={3}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Target Organ
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {trial.targetOrgan}
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Match Reasons */}
        {matchReasons && matchReasons.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="subtitle2"
              gutterBottom
              color="success.main"
              fontWeight="bold"
            >
              Why This Trial Matches:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {matchReasons.map((reason, idx) => (
                <Chip
                  key={idx}
                  icon={<CheckCircleIcon />}
                  label={reason}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          </>
        )}

        {/* Brief Summary */}
        {trial.briefSummary && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {trial.briefSummary.length > 250
                ? `${trial.briefSummary.substring(0, 250)}...`
                : trial.briefSummary}
            </Typography>
          </>
        )}

        {/* View Details Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant={expanded ? "outlined" : "contained"}
            color={getMatchColor(matchScore)}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide Full Details" : "View Full Trial Details"}
          </Button>
        </Box>

        {/* Expanded Details */}
        {expanded && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            {trial.eligibilityCriteria && (
              <>
                <Typography variant="h6" gutterBottom color="primary">
                  Eligibility Criteria
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {trial.eligibilityCriteria}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {trial.description && (
              <>
                <Typography variant="h6" gutterBottom>
                  Detailed Description
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {trial.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {trial.locations && (
              <>
                <Typography variant="h6" gutterBottom>
                  Trial Locations
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {trial.locations}
                </Typography>
              </>
            )}

            <Button
              fullWidth
              variant="contained"
              color="primary"
              href={`https://clinicaltrials.gov/study/${trial.nctId}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 2 }}
            >
              View on ClinicalTrials.gov
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PatientMatchingPage;
